import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { cn, useSearchScopeStore } from '@/shared/lib';
import { GLYPH, IconSearch, Kbd } from '@/shared/ui';

const SEARCH_PARAM = 'q';
const DEBOUNCE_MS = 300;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/**
 * Поиск по разделу (README §1): 260×32, radius 16, рамка line-field, иконка 16 ink-400,
 * подсказка клавиши «/». Пишет `?q=` в URL и сбрасывает страницу на первую.
 * Активен, только если текущий экран зарегистрировал область поиска (реестр раздела).
 */
export function TopbarSearch() {
  const { t } = useTranslation('shell');
  const scope = useSearchScopeStore((state) => state.label);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(SEARCH_PARAM) ?? '';
  const [text, setText] = useState(urlValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const enabled = scope !== null;

  useEffect(() => {
    setText(urlValue);
  }, [urlValue]);

  useEffect(() => {
    if (!enabled || text.trim() === urlValue) return undefined;
    const timer = window.setTimeout(() => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          const value = text.trim();
          if (value) next.set(SEARCH_PARAM, value);
          else next.delete(SEARCH_PARAM);
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, text, urlValue, setSearchParams]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target) || !inputRef.current || inputRef.current.disabled) return;
      event.preventDefault();
      inputRef.current.focus();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const placeholder = scope
    ? t('search.scoped', { section: scope.toLocaleLowerCase() })
    : t('search.placeholder');

  return (
    <search
      className={cn(
        'flex h-32 w-260 shrink-0 items-center gap-8 rounded-16 border border-line-field bg-paper-pure px-10',
        'focus-within:border-wine-bordeaux focus-within:shadow-focus-field',
        enabled ? undefined : 'opacity-60',
      )}
      title={enabled ? t('search.hotkeyHint') : t('search.unavailable')}
    >
      <IconSearch size={16} className="shrink-0 text-ink-400" />
      <input
        ref={inputRef}
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={placeholder}
        disabled={!enabled}
        aria-label={placeholder}
        className="min-w-0 flex-1 border-0 bg-transparent font-ui text-[13px] text-ink-900 outline-none placeholder:text-ink-400 focus-visible:outline-none disabled:cursor-not-allowed [&::-webkit-search-cancel-button]:hidden"
      />
      <Kbd>{GLYPH.slash}</Kbd>
    </search>
  );
}
